// Unified Dashboards — the 3 built-in views (Operations / NOC / Executive) plus
// the user's CUSTOM dashboards (Part C): server-backed named tabs, private /
// public / shared (per RBAC). Built-ins persist to localStorage; custom boards
// persist their layout to the server. The last-opened dashboard is remembered in
// user preferences so it reopens where you left off.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LayoutDashboard, Briefcase, LineChart, Plus, Share2, Trash2, X, ChevronDown } from 'lucide-react';
import { WidgetGrid } from '@/components/dashboard-grid/WidgetGrid';
import type { GridItem, CustomSource } from '@/components/dashboard-grid/useDashboardLayout';
import { dashboardsApi, type DashboardSummary, type DashboardVisibility } from '@/lib/dashboards-api';
import { userPreferencesApi } from '@/services/api';
import { ConfirmDialog } from '@/components/ui/dialogs';

type BuiltinView = 'operations' | 'noc' | 'executive';
const BUILTINS: Array<{ key: BuiltinView; labelKey: string; icon: typeof LayoutDashboard; dashboard: string; path: string }> = [
  { key: 'operations', labelKey: 'views.operations', icon: LayoutDashboard, dashboard: 'operations', path: '/' },
  { key: 'noc', labelKey: 'views.provider', icon: Briefcase, dashboard: 'provider', path: '/provider' },
  { key: 'executive', labelKey: 'views.executive', icon: LineChart, dashboard: 'executive', path: '/executive' },
];
const SHAREABLE_ROLES = ['tenant_admin', 'project_manager', 'operator', 'viewer'];

function builtinForPath(pathname: string): BuiltinView {
  if (pathname.startsWith('/provider')) return 'noc';
  if (pathname.startsWith('/executive')) return 'executive';
  return 'operations';
}

export function DashboardsPage() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');
  const qc = useQueryClient();

  // Selected tab: a builtin key OR a custom dashboard id.
  const [selected, setSelected] = useState<string>(() => builtinForPath(pathname));
  const [newOpen, setNewOpen] = useState(false);
  const [shareFor, setShareFor] = useState<DashboardSummary | null>(null);
  const [deleteFor, setDeleteFor] = useState<DashboardSummary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close the view dropdown on outside click / Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => { if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [menuOpen]);

  // Built-in routes drive selection (so deep links still work).
  useEffect(() => { if (['/', '/provider', '/executive'].includes(pathname)) setSelected(builtinForPath(pathname)); }, [pathname]);

  const { data: customs = [], refetch } = useQuery({
    queryKey: ['dashboards'], queryFn: dashboardsApi.list, retry: false,
  });

  // Restore last-opened dashboard once, on first load.
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    void userPreferencesApi.getUiSettings().then((ui) => {
      const last = ui.lastDashboard as string | undefined;
      if (last && BUILTINS.some((b) => b.key === last)) setSelected(last);
      else if (last) setSelected(last); // a custom id; validated on render
    }).catch(() => {});
  }, []);

  // Remember the selection.
  useEffect(() => {
    void userPreferencesApi.updateUiSettings({ lastDashboard: selected }).catch(() => {});
  }, [selected]);

  const selectBuiltin = (b: typeof BUILTINS[number]) => { setSelected(b.key); navigate(b.path); };
  const customSelected = customs.find((c) => c.id === selected);

  // View selector — a dropdown of the 3 built-ins + the user's custom boards
  // + "New". Replaces the old segmented tab row (which got long with many
  // custom dashboards). Built-ins route (deep links keep working); customs
  // just set `selected`.
  const selectedBuiltin = BUILTINS.find((b) => b.key === selected);
  const currentLabel = selectedBuiltin ? t(selectedBuiltin.labelKey) : (customSelected?.name ?? t(BUILTINS[0].labelKey));
  const CurrentIcon = selectedBuiltin?.icon ?? LayoutDashboard;
  const selector = (
    <div ref={menuRef} className="relative w-fit text-sm">
      <button
        onClick={() => setMenuOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        className="flex items-center gap-2 rounded-md bg-white/5 px-3 py-1.5 ring-1 ring-white/10 hover:bg-white/10"
      >
        <CurrentIcon size={15} className="text-primary" />
        <span className="font-medium">{currentLabel}</span>
        <ChevronDown size={14} className={`text-muted transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
      </button>
      {menuOpen && (
        <div role="menu" className="tk-modal absolute z-30 mt-1 w-60 overflow-hidden p-1 shadow-xl">
          {BUILTINS.map(({ key, labelKey, icon: Icon, path }) => (
            <button
              key={key}
              role="menuitem"
              onClick={() => { setMenuOpen(false); selectBuiltin({ key, labelKey, icon: Icon, dashboard: '', path } as typeof BUILTINS[number]); }}
              className={`flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-left transition-colors ${selected === key ? 'bg-white/10 text-fg' : 'text-muted hover:bg-white/5 hover:text-fg'}`}
            >
              <Icon size={15} /> {t(labelKey)}
            </button>
          ))}
          {customs.length > 0 && <div className="my-1 border-t border-white/10" />}
          {customs.map((c) => (
            <button
              key={c.id}
              role="menuitem"
              onClick={() => { setMenuOpen(false); setSelected(c.id); }}
              title={c.description ?? undefined}
              className={`flex w-full items-center justify-between gap-2 rounded px-2.5 py-1.5 text-left transition-colors ${selected === c.id ? 'bg-white/10 text-fg' : 'text-muted hover:bg-white/5 hover:text-fg'}`}
            >
              <span className="truncate">{c.name}</span>
              {c.visibility !== 'private' && <span className="shrink-0 text-[10px] text-muted/60">{t(`custom.vis.${c.visibility}`)}</span>}
            </button>
          ))}
          <div className="my-1 border-t border-white/10" />
          <button
            role="menuitem"
            onClick={() => { setMenuOpen(false); setNewOpen(true); }}
            className="flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-left text-muted hover:bg-white/5 hover:text-fg"
          ><Plus size={15} /> {t('custom.new')}</button>
        </div>
      )}
    </div>
  );

  // Per-custom-dashboard toolbar actions (share + delete).
  const customActions = customSelected ? (
    <div className="flex items-center gap-1">
      <button onClick={() => setShareFor(customSelected)} className="tk-btn-ghost px-2" title={t('custom.share')}><Share2 size={15} /></button>
      <button
        onClick={() => setDeleteFor(customSelected)}
        className="tk-btn-ghost px-2 hover:text-critical" title={t('custom.delete')}
      ><Trash2 size={15} /></button>
    </div>
  ) : null;

  return (
    <>
      {customSelected
        ? <CustomDashboard key={customSelected.id} id={customSelected.id} name={customSelected.name} selector={selector} actions={customActions} />
        : <BuiltinDashboard view={selected as BuiltinView} selector={selector} />}

      {newOpen && <NewDashboardModal onClose={() => setNewOpen(false)} onCreated={(id) => { setNewOpen(false); qc.invalidateQueries({ queryKey: ['dashboards'] }); setSelected(id); }} />}
      {shareFor && <ShareModal dashboard={shareFor} onClose={() => setShareFor(null)} onSaved={() => { setShareFor(null); refetch(); }} />}
      {deleteFor && (
        <ConfirmDialog
          title={t('custom.deleteTitle')}
          message={t('custom.deleteConfirm', { name: deleteFor.name })}
          confirmLabel={t('custom.delete')}
          busy={deleting}
          onConfirm={async () => {
            setDeleting(true);
            try { await dashboardsApi.remove(deleteFor.id); setSelected('operations'); navigate('/'); refetch(); setDeleteFor(null); }
            finally { setDeleting(false); }
          }}
          onClose={() => setDeleteFor(null)}
        />
      )}
    </>
  );
}

function BuiltinDashboard({ view, selector }: { view: BuiltinView; selector: React.ReactNode }) {
  const { t } = useTranslation('dashboard');
  if (view === 'noc') return <WidgetGrid selector={selector} dashboard="provider" title={t('provider.title')} subtitle={t('provider.subtitle')} />;
  if (view === 'executive') return <WidgetGrid selector={selector} dashboard="executive" title={t('executive.title')} subtitle={t('executive.subtitle')} />;
  return <WidgetGrid selector={selector} dashboard="operations" title={t('operations.title')} subtitle={t('operations.subtitle')} />;
}

function CustomDashboard({ id, name, selector, actions }: { id: string; name: string; selector: React.ReactNode; actions: React.ReactNode }) {
  const { t } = useTranslation('dashboard');
  const { data } = useQuery({ queryKey: ['dashboard', id], queryFn: () => dashboardsApi.get(id), retry: false });

  // Debounced server save of the layout config.
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const source: CustomSource | undefined = useMemo(() => {
    if (!data) return undefined;
    const initialItems = (data.config?.items as GridItem[] | undefined) ?? [];
    return {
      catalog: 'operations',
      initialItems,
      onSave: (items) => {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => { void dashboardsApi.update(id, { config: { items } }).catch(() => {}); }, 600);
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id, JSON.stringify((data?.config?.items as GridItem[] | undefined)?.map((i) => i.i))]);

  if (!source) return <div className="p-6 text-sm text-muted">{t('custom.loading')}</div>;
  return <WidgetGrid selector={selector} actions={actions} custom={source} dashboard="operations" title={name} subtitle={t('custom.subtitle')} />;
}

// ── Modals ───────────────────────────────────────────────────────────────────
function NewDashboardModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const { t } = useTranslation('dashboard');
  const [name, setName] = useState('');
  const [visibility, setVisibility] = useState<DashboardVisibility>('private');
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const d = await dashboardsApi.create({ name: name.trim(), visibility, config: { items: [] } });
      onCreated(d.id);
    } finally { setBusy(false); }
  };
  return (
    <div className="tk-modal-overlay" onClick={() => !busy && onClose()}>
      <form className="tk-modal w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="mb-3 flex items-center justify-between"><h2 className="text-base font-semibold">{t('custom.newTitle')}</h2><button type="button" className="tk-btn-ghost px-2" onClick={onClose}><X size={16} /></button></div>
        <label className="text-xs text-muted">{t('custom.nameLabel')}<input autoFocus className="tk-input mt-1 w-full py-1.5 text-sm" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('custom.namePlaceholder')} /></label>
        <fieldset className="mt-3">
          <legend className="text-xs text-muted">{t('custom.visLabel')}</legend>
          <div className="mt-1 flex gap-1 rounded-md bg-white/5 p-0.5 text-sm ring-1 ring-white/10">
            {(['private', 'public', 'shared'] as DashboardVisibility[]).map((v) => (
              <button type="button" key={v} onClick={() => setVisibility(v)} className={`flex-1 rounded px-2 py-1.5 ${visibility === v ? 'bg-white/10 text-fg' : 'text-muted hover:text-fg'}`}>{t(`custom.vis.${v}`)}</button>
            ))}
          </div>
          <p className="mt-1 text-xs text-muted/70">{t(`custom.visHint.${visibility}`)}</p>
        </fieldset>
        <div className="mt-4 flex justify-end gap-2"><button type="button" className="tk-btn-ghost" disabled={busy} onClick={onClose}>{t('custom.cancel')}</button><button type="submit" className="tk-btn-primary" disabled={busy || !name.trim()}>{busy ? t('custom.creating') : t('custom.create')}</button></div>
      </form>
    </div>
  );
}

function ShareModal({ dashboard, onClose, onSaved }: { dashboard: DashboardSummary; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation('dashboard');
  const [visibility, setVisibility] = useState<DashboardVisibility>(dashboard.visibility);
  const [roles, setRoles] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const toggleRole = (r: string) => setRoles((cur) => (cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]));
  const save = async () => {
    setBusy(true);
    try {
      await dashboardsApi.update(dashboard.id, { visibility });
      if (visibility === 'shared') await dashboardsApi.setPermissions(dashboard.id, roles.map((role) => ({ role, canEdit: true })));
      else await dashboardsApi.setPermissions(dashboard.id, []);
      onSaved();
    } finally { setBusy(false); }
  };
  return (
    <div className="tk-modal-overlay" onClick={() => !busy && onClose()}>
      <div className="tk-modal w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between"><h2 className="text-base font-semibold">{t('custom.shareTitle', { name: dashboard.name })}</h2><button className="tk-btn-ghost px-2" onClick={onClose}><X size={16} /></button></div>
        <fieldset>
          <legend className="text-xs text-muted">{t('custom.visLabel')}</legend>
          <div className="mt-1 flex gap-1 rounded-md bg-white/5 p-0.5 text-sm ring-1 ring-white/10">
            {(['private', 'public', 'shared'] as DashboardVisibility[]).map((v) => (
              <button key={v} onClick={() => setVisibility(v)} className={`flex-1 rounded px-2 py-1.5 ${visibility === v ? 'bg-white/10 text-fg' : 'text-muted hover:text-fg'}`}>{t(`custom.vis.${v}`)}</button>
            ))}
          </div>
          <p className="mt-1 text-xs text-muted/70">{t(`custom.visHint.${visibility}`)}</p>
        </fieldset>
        {visibility === 'shared' && (
          <fieldset className="mt-3">
            <legend className="text-xs text-muted">{t('custom.shareRoles')}</legend>
            <div className="mt-1 space-y-1">
              {SHAREABLE_ROLES.map((r) => (
                <label key={r} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm ring-1 ring-white/10 hover:bg-white/5">
                  <input type="checkbox" checked={roles.includes(r)} onChange={() => toggleRole(r)} />
                  <span>{r}</span>
                </label>
              ))}
            </div>
          </fieldset>
        )}
        <div className="mt-4 flex justify-end gap-2"><button className="tk-btn-ghost" disabled={busy} onClick={onClose}>{t('custom.cancel')}</button><button className="tk-btn-primary" disabled={busy} onClick={save}>{busy ? t('custom.saving') : t('custom.saveShare')}</button></div>
      </div>
    </div>
  );
}
